/**
 * CR096: Hook for fetching dynamic merchant categories from the database
 * Only returns categories that have deals available
 * 
 * FIX: Categories are now extracted directly from deal data OR looked up
 * by normalizing merchant names to keys for consistency.
 * 
 * CR403: Extended with admin CRUD operations for merchant_categories table
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getMerchant, isMerchantIndexLoaded, getAllMerchants } from "@/lib/merchantIndex";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { Deal } from "@/lib/types/deals";

// ============================================================================
// CR096: Original hook for deal-based category filtering (consumer-facing)
// ============================================================================

export interface MerchantCategory {
  category: string;
  count: number;
}

interface UseMerchantCategoriesOptions {
  deals?: Deal[];
}

/**
 * Normalize a merchant display name to a key format for lookup
 * "Walmart" -> "walmart", "No Frills" -> "no-frills"
 */
function normalizeToMerchantKey(merchantName: string): string {
  return merchantName
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/**
 * Get category for a deal using multiple fallback strategies:
 * 1. Direct lookup by normalized merchant name
 * 2. Partial match against merchant index
 * 3. Return null if no match found
 */
function getDealCategory(deal: Deal): string | null {
  if (!deal.merchant) return null;
  
  const merchantName = deal.merchant;
  const normalizedKey = normalizeToMerchantKey(merchantName);
  
  // Strategy 1: Direct lookup by normalized key
  const merchant = getMerchant(normalizedKey);
  if (merchant?.category) {
    return merchant.category;
  }
  
  // Strategy 2: Try exact match on display_name (case-insensitive)
  const allMerchants = getAllMerchants();
  for (const m of allMerchants) {
    if (m.display_name.toLowerCase() === merchantName.toLowerCase()) {
      if (m.category) return m.category;
    }
  }
  
  // Strategy 3: Try partial match (merchant name contains or is contained)
  for (const m of allMerchants) {
    const displayLower = m.display_name.toLowerCase();
    const nameLower = merchantName.toLowerCase();
    if (displayLower.includes(nameLower) || nameLower.includes(displayLower)) {
      if (m.category) return m.category;
    }
  }
  
  return null;
}

export function useMerchantCategories(options: UseMerchantCategoriesOptions = {}) {
  const { deals = [] } = options;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Compute categories based on deals that actually exist
  const { categories, debugInfo } = useMemo(() => {
    const info = {
      totalDeals: deals.length,
      dealsWithCategory: 0,
      dealsMissingCategory: 0,
      categoryBreakdown: {} as Record<string, number>,
    };
    
    if (!isMerchantIndexLoaded() || deals.length === 0) {
      return { categories: [], debugInfo: info };
    }

    // Count deals per category using the unified extraction function
    const categoryDealCount = new Map<string, number>();
    
    for (const deal of deals) {
      const category = getDealCategory(deal);
      
      if (category) {
        info.dealsWithCategory++;
        const currentCount = categoryDealCount.get(category) || 0;
        categoryDealCount.set(category, currentCount + 1);
      } else {
        info.dealsMissingCategory++;
        // Count under "Unclassified" so totals add up
        const unclassifiedCount = categoryDealCount.get("Unclassified") || 0;
        categoryDealCount.set("Unclassified", unclassifiedCount + 1);
      }
    }
    
    // Build debug breakdown
    for (const [cat, count] of categoryDealCount.entries()) {
      info.categoryBreakdown[cat] = count;
    }

    // Convert to sorted array - only include categories with deals
    const cats = Array.from(categoryDealCount.entries())
      .filter(([_, count]) => count > 0)
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => {
        // Put "Unclassified" at the end
        if (a.category === "Unclassified") return 1;
        if (b.category === "Unclassified") return -1;
        return a.category.localeCompare(b.category);
      });
    
    // Log debug info in development
    console.log("[useMerchantCategories] Debug:", {
      totalDeals: info.totalDeals,
      dealsWithCategory: info.dealsWithCategory,
      dealsMissingCategory: info.dealsMissingCategory,
      categoryBreakdown: info.categoryBreakdown,
      sumOfCategories: cats.reduce((sum, c) => sum + c.count, 0),
    });
    
    return { categories: cats, debugInfo: info };
  }, [deals]);

  // Update loading state based on merchant index
  useEffect(() => {
    const checkLoaded = () => {
      if (isMerchantIndexLoaded()) {
        setLoading(false);
        setError(null);
      }
    };
    
    // Check immediately
    checkLoaded();
    
    // Also check after a short delay in case it's still loading
    const timeout = setTimeout(checkLoaded, 100);
    return () => clearTimeout(timeout);
  }, [deals]);

  // Get just the category names as an array
  const categoryNames = useMemo(() => categories.map(c => c.category), [categories]);

  const refetch = useCallback(() => {
    // No-op since we compute from deals now
  }, []);

  return {
    categories,
    categoryNames,
    loading,
    error,
    refetch,
    debugInfo,
  };
}

// ============================================================================
// CR403: Admin hooks for merchant_categories table CRUD
// ============================================================================

export interface MerchantCategoryRow {
  id: string;
  name: string;
  slug: string;
  icon_asset_id: string | null;
  icon_url: string | null;
  icon_svg: string | null;
  icon_source: string | null;
  icon_attribution: string | null;
  created_at: string;
  updated_at: string;
  merchants_count: number;
  sources_override_count: number;
  sources_inherited_count: number;
  sources_effective_count: number;
}

const CATEGORIES_KEY = ["merchant-categories"];

/**
 * Fetch all categories with usage counts (admin view)
 */
export function useMerchantCategoriesList() {
  return useQuery({
    queryKey: CATEGORIES_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_merchant_categories_usage")
        .select("*")
        .order("name", { ascending: true });

      if (error) throw error;
      return (data ?? []) as MerchantCategoryRow[];
    },
  });
}

/**
 * Hook providing category mutations (admin)
 */
export function useMerchantCategoryMutations() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const invalidateCategories = () => {
    queryClient.invalidateQueries({ queryKey: CATEGORIES_KEY });
  };

  // Create category
  const createMutation = useMutation({
    mutationFn: async ({ name, slug, iconAssetId }: { 
      name: string; 
      slug?: string; 
      iconAssetId?: string | null;
    }) => {
      const { data, error } = await supabase.rpc("admin_create_category", {
        p_name: name,
        p_slug: slug || null,
        p_icon_asset_id: iconAssetId || null,
      });
      if (error) throw error;
      return data as string; // Returns the new category ID
    },
    onSuccess: () => {
      toast({ title: "Category created", description: "New category added successfully" });
      invalidateCategories();
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  // Update category
  const updateMutation = useMutation({
    mutationFn: async ({ 
      categoryId, 
      name, 
      slug, 
      iconAssetId,
      iconUrl,
      iconSvg,
      iconSource,
      iconAttribution,
    }: { 
      categoryId: string; 
      name?: string; 
      slug?: string; 
      iconAssetId?: string | null;
      iconUrl?: string | null;
      iconSvg?: string | null;
      iconSource?: string | null;
      iconAttribution?: string | null;
    }) => {
      const { error } = await supabase.rpc("admin_update_category", {
        p_category_id: categoryId,
        p_name: name || null,
        p_slug: slug || null,
        p_icon_asset_id: iconAssetId,
        p_icon_url: iconUrl,
        p_icon_svg: iconSvg,
        p_icon_source: iconSource,
        p_icon_attribution: iconAttribution,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Category updated", description: "Category saved successfully" });
      invalidateCategories();
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  // Delete category with reassignment
  const deleteMutation = useMutation({
    mutationFn: async ({ 
      oldCategoryId, 
      newCategoryId, 
      clearSourceOverrides = false 
    }: { 
      oldCategoryId: string; 
      newCategoryId: string; 
      clearSourceOverrides?: boolean;
    }) => {
      const { data, error } = await supabase.rpc("admin_delete_category_with_reassign", {
        p_old_category_id: oldCategoryId,
        p_new_category_id: newCategoryId,
        p_clear_source_overrides: clearSourceOverrides,
      });
      if (error) throw error;
      return data as {
        success: boolean;
        deleted_category: string;
        replacement_category: string;
        merchants_moved: number;
        overrides_moved: number;
        overrides_cleared: number;
      };
    },
    onSuccess: (result) => {
      toast({ 
        title: "Category deleted", 
        description: `Moved ${result.merchants_moved} merchants to "${result.replacement_category}"` 
      });
      invalidateCategories();
      // Also invalidate merchant-related queries
      queryClient.invalidateQueries({ queryKey: ["merchants-directory"] });
      queryClient.invalidateQueries({ queryKey: ["merchant-sources"] });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  // Set merchant's category
  const setMerchantCategoryMutation = useMutation({
    mutationFn: async ({ merchantKey, categoryId }: { merchantKey: string; categoryId: string | null }) => {
      const { error } = await supabase.rpc("admin_set_merchant_category", {
        p_merchant_key: merchantKey,
        p_category_id: categoryId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Category updated", description: "Merchant category saved" });
      invalidateCategories();
      queryClient.invalidateQueries({ queryKey: ["merchants-directory"] });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  // Set source category override
  const setSourceCategoryOverrideMutation = useMutation({
    mutationFn: async ({ sourceId, categoryOverrideId }: { sourceId: string; categoryOverrideId: string | null }) => {
      const { error } = await supabase.rpc("admin_set_source_category_override", {
        p_source_id: sourceId,
        p_category_override_id: categoryOverrideId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Override updated", description: "Source category override saved" });
      invalidateCategories();
      queryClient.invalidateQueries({ queryKey: ["merchant-sources"] });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  return {
    createCategory: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateCategory: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteCategory: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    setMerchantCategory: setMerchantCategoryMutation.mutateAsync,
    isSettingMerchantCategory: setMerchantCategoryMutation.isPending,
    setSourceCategoryOverride: setSourceCategoryOverrideMutation.mutateAsync,
    isSettingSourceOverride: setSourceCategoryOverrideMutation.isPending,
  };
}
